// src/types/storeReport.ts
//
// Shopper-submitted reports about a business (shop, stay, restaurant).
//
// Two-tier ownership (mirrors the fix-it complaint model):
//   - `marketplace` concerns (safety, conduct, no-show, fraud) are OURS: they
//     go to the admin inbox and can lead to a warn/pause/remove.
//   - `commercial` concerns (price, quality, not-as-described) are NOT ours:
//     they are recorded and shared with the owner so the two parties settle it.
//     Zamzi holds no money and makes no ruling on these.
//
// Reports NEVER auto-freeze a business — a human always decides.

export type StoreReportOwnership = 'marketplace' | 'commercial';

export type StoreReportVerticalKey = 'retail' | 'stay' | 'restaurant' | 'other';

export type StoreReportCategory =
  | 'not_received'
  | 'not_as_described'
  | 'fraud'
  | 'safety'
  | 'not_honoured'
  | 'hygiene'
  | 'quality'
  | 'price'
  | 'other';

export type StoreReportStatus = 'open' | 'reviewing' | 'resolved' | 'dismissed';

export type StoreReportAlertStatus = 'sent' | 'failed' | 'not_configured';

export type StoreReportCategoryDef = {
  value: StoreReportCategory;
  label: string;
  ownership: StoreReportOwnership;
};

export type StoreReportVerticalConfig = {
  /** Short noun used in copy ("shop", "stay", "restaurant"). */
  noun: string;
  categories: StoreReportCategoryDef[];
};

export type StoreReportRecord = {
  id: string;
  vertical: string;
  profileId: string;
  shopName: string;
  category: StoreReportCategory;
  /** Who owns the outcome: Zamzi (marketplace) or the owner (commercial). */
  ownership: StoreReportOwnership;
  description: string;
  reporterName?: string;
  reporterEmail?: string;
  reporterPhone?: string;
  /** True when the reporter's email matches a real paid order/booking. */
  isVerifiedBuyer: boolean;
  verifiedOrderId?: string;
  status: StoreReportStatus;
  adminNote?: string;
  /** Whether the "new report" alert email reached the report inbox. */
  alertEmailStatus?: StoreReportAlertStatus;
  alertEmailError?: string;
  /** Whether a commercial report was emailed to the business owner. */
  ownerNotified?: boolean;
  ownerNotifiedAtUTC?: string;
  /** True when the listing was auto-frozen pending review (verified guest + fraud/not-honoured). */
  autoFrozeListing?: boolean;
  autoFrozeAtUTC?: string;
  raisedAtUTC: string;
  updatedAtUTC: string;
};

export type CreateStoreReportInput = {
  vertical: string;
  profileId: string;
  shopName: string;
  category: StoreReportCategory;
  ownership: StoreReportOwnership;
  description: string;
  reporterName?: string;
  reporterEmail?: string;
  reporterPhone?: string;
  isVerifiedBuyer: boolean;
  verifiedOrderId?: string;
  autoFrozeListing?: boolean;
};

const RETAIL_CATEGORIES: StoreReportCategoryDef[] = [
  { value: 'not_received', label: 'I paid but did not receive my order', ownership: 'marketplace' },
  { value: 'not_as_described', label: 'The item was not as described', ownership: 'commercial' },
  { value: 'fraud', label: 'Fraud or scam', ownership: 'marketplace' },
  { value: 'safety', label: 'Safety or abusive conduct', ownership: 'marketplace' },
  { value: 'other', label: 'Something else', ownership: 'commercial' },
];

const STAY_CATEGORIES: StoreReportCategoryDef[] = [
  { value: 'not_honoured', label: 'My booking was not honoured', ownership: 'marketplace' },
  { value: 'not_as_described', label: 'The place was not as described', ownership: 'commercial' },
  { value: 'hygiene', label: 'Hygiene or safety problem', ownership: 'marketplace' },
  { value: 'fraud', label: 'Fraud or scam', ownership: 'marketplace' },
  { value: 'price', label: 'Disagreement about the price', ownership: 'commercial' },
  { value: 'other', label: 'Something else', ownership: 'commercial' },
];

const RESTAURANT_CATEGORIES: StoreReportCategoryDef[] = [
  { value: 'not_received', label: 'I paid but the order never arrived', ownership: 'marketplace' },
  { value: 'not_as_described', label: 'The food was not as described', ownership: 'commercial' },
  { value: 'hygiene', label: 'Food safety or hygiene', ownership: 'marketplace' },
  { value: 'safety', label: 'Unsafe or abusive conduct', ownership: 'marketplace' },
  { value: 'fraud', label: 'Fraud or scam', ownership: 'marketplace' },
  { value: 'price', label: 'Disagreement about the price', ownership: 'commercial' },
  { value: 'other', label: 'Something else', ownership: 'commercial' },
];

export const STORE_REPORT_VERTICALS: Record<StoreReportVerticalKey, StoreReportVerticalConfig> = {
  retail: { noun: 'shop', categories: RETAIL_CATEGORIES },
  stay: { noun: 'stay', categories: STAY_CATEGORIES },
  restaurant: { noun: 'restaurant', categories: RESTAURANT_CATEGORIES },
  other: { noun: 'business', categories: RETAIL_CATEGORIES },
};

export const isStoreReportVerticalKey = (value: unknown): value is StoreReportVerticalKey =>
  typeof value === 'string' && value in STORE_REPORT_VERTICALS;

export const getStoreReportCategories = (
  vertical: StoreReportVerticalKey | string,
): StoreReportCategoryDef[] =>
  (isStoreReportVerticalKey(vertical) ? STORE_REPORT_VERTICALS[vertical] : STORE_REPORT_VERTICALS.other)
    .categories;

export const STORE_REPORT_CATEGORY_LABELS: Record<StoreReportCategory, string> = {
  not_received: 'Paid but not received',
  not_as_described: 'Not as described',
  fraud: 'Fraud or scam',
  safety: 'Safety or conduct',
  not_honoured: 'Booking not honoured',
  hygiene: 'Hygiene or safety',
  quality: 'Quality problem',
  price: 'Price disagreement',
  other: 'Other',
};

export const isStoreReportCategory = (value: unknown): value is StoreReportCategory =>
  typeof value === 'string' && value in STORE_REPORT_CATEGORY_LABELS;

/**
 * Fail-safe: an unknown vertical/category is treated as a marketplace concern so
 * it lands with the admin rather than being silently shared with the owner.
 */
export const getStoreReportOwnership = (
  vertical: StoreReportVerticalKey | string,
  category: StoreReportCategory,
): StoreReportOwnership => {
  const def = getStoreReportCategories(vertical).find((c) => c.value === category);
  return def?.ownership ?? 'marketplace';
};
